import React, { useEffect, useState } from 'react';
import './App.css';
import { trackPurchase } from './GoogleAnalytics';
import { trackConversion } from './ConversionTracking';

export default function BusinessSuccessPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [total, setTotal] = useState('');
  // Per-step outcomes forwarded by the order form. Absent means the step was
  // fine, so an older link or a hand-typed URL still renders the normal page.
  const [receiptFailed, setReceiptFailed] = useState(false);
  const [artworkFailed, setArtworkFailed] = useState(false);
  // Track viewport so layout reacts to resize/rotation instead of freezing at mount
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Get order number from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const order = urlParams.get('order');
    const totalParam = urlParams.get('total');
    if (order) {
      setOrderNumber(order);
    }
    if (totalParam) {
      setTotal(totalParam);
    }
    setReceiptFailed(urlParams.get('receipt') === 'failed');
    setArtworkFailed(urlParams.get('artwork') === 'failed');

    // Track the business order as a conversion (GA4 + Google Ads + Meta Pixel).
    // Ad conversions are a no-op until the ad IDs are set in env.
    const value = parseFloat(totalParam);
    if (order && !Number.isNaN(value)) {
      trackPurchase(order, value, 'USD', []);
      trackConversion({ transactionId: order, value, currency: 'USD' });
    }
  }, []);

  const displayOrderNumber = orderNumber ? orderNumber.replace(/^BIZ#/, '') : '';

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{ 
        background: 'white', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '1.5rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
            <img
              src="/PressPeelsHeader.png"
              alt="Press Peels Logo"
              style={{ height: '48px', width: 'auto', maxWidth: '100%' }}
            />
            <div style={{ minWidth: 0 }}>
              <h1 style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold', 
                color: '#111827',
                margin: '0 0 0.25rem 0'
              }}>
                Business Orders
              </h1>
              <p style={{ 
                color: '#6b7280', 
                margin: 0,
                fontSize: '0.9rem'
              }}>
                Custom Press Peels for Business Clients
              </p>
            </div>
          </div>
          <a
            href="/"
            style={{
              color: '#2563eb',
              textDecoration: 'none',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: '44px',
              padding: '0 0.5rem'
            }}
          >
            ← Back to Main Site
          </a>
        </div>
      </div>

      {/* Success Message */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '3rem 1rem' 
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {/* Success Icon */}
          <div style={{ 
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '64px',
            width: '64px',
            borderRadius: '50%',
            background: '#dcfce7',
            marginBottom: '1.5rem'
          }}>
            <div style={{ 
              fontSize: '2rem',
              color: '#059669'
            }}>
              ✅
            </div>
          </div>
          
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: 'bold', 
            color: '#111827',
            marginBottom: '1rem',
            margin: '0 0 1rem 0'
          }}>
            Business Order Submitted Successfully!
          </h1>
          
          {(orderNumber || total) && (
            <div style={{ 
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              padding: '1.5rem',
              marginBottom: '2rem',
              display: 'inline-block'
            }}>
              {displayOrderNumber && (
                <>
                  <div style={{ 
                    fontSize: '1.125rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Your Order Number:
                  </div>
                  <div style={{ 
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#2563eb',
                    marginBottom: total ? '0.75rem' : 0
                  }}>
                    #{displayOrderNumber}
                  </div>
                </>
              )}
              {total && (
                <div style={{ 
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#059669'
                }}>
                  Total: ${parseFloat(total).toFixed(2)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Something didn't complete.
            The heading above still says the order was submitted, because it
            was — the number is issued and the job is ours. But any step that
            failed is named here with what the customer should do about it.
            Staying quiet is what hurt on 2026-08-26: the page claimed a receipt
            had been sent, so the customer had no reason to expect silence and
            no reason to chase it. */}
        {(receiptFailed || artworkFailed) && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '700',
              color: '#92400e',
              margin: '0 0 0.75rem 0'
            }}>
              ⚠️ Your order is placed — but not everything went through
            </h2>

            {displayOrderNumber && (
              <p style={{ color: '#78350f', fontSize: '0.95rem', margin: '0 0 0.75rem 0', lineHeight: 1.6 }}>
                Please write down your order number, <strong>#{displayOrderNumber}</strong>.
                Do not submit this order again — we already have it.
              </p>
            )}

            {receiptFailed && (
              <p style={{ color: '#78350f', fontSize: '0.95rem', margin: '0 0 0.75rem 0', lineHeight: 1.6 }}>
                <strong>Your confirmation email didn't send.</strong> Our email is
                having trouble at the moment. This one is on us, not your
                connection, and it does not affect your order or your delivery
                date — your confirmation will arrive once the problem clears.
              </p>
            )}

            {artworkFailed && (
              <p style={{ color: '#78350f', fontSize: '0.95rem', margin: '0 0 0.75rem 0', lineHeight: 1.6 }}>
                <strong>Some of your artwork didn't finish uploading.</strong> We
                can't begin printing without it. Please email your files to{' '}
                <a href="mailto:support@presspeels.com" style={{ color: '#92400e', fontWeight: '600' }}>
                  support@presspeels.com
                </a>{' '}
                and we'll take it from there.
              </p>
            )}

            <p style={{ color: '#78350f', fontSize: '0.95rem', margin: 0, lineHeight: 1.6 }}>
              Questions? Email{' '}
              <a href="mailto:support@presspeels.com" style={{ color: '#92400e', fontWeight: '600' }}>
                support@presspeels.com
              </a>
              {displayOrderNumber ? ` and quote #${displayOrderNumber}.` : '.'}
            </p>
          </div>
        )}

        {/* What Happens Next */}
        <div style={{ 
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ 
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '1.5rem',
            textAlign: 'center',
            margin: '0 0 1.5rem 0'
          }}>
            📋 What Happens Next?
          </h2>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: '1.5rem'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ 
                  flexShrink: 0,
                  width: '32px',
                  height: '32px',
                  background: '#dbeafe',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: '#2563eb', fontWeight: '600' }}>1</span>
                </div>
                <div>
                  <h3 style={{ 
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Order Received
                  </h3>
                  <p style={{ 
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    margin: 0
                  }}>
                    Your business order has been received and is being processed
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ 
                  flexShrink: 0,
                  width: '32px',
                  height: '32px',
                  background: '#dbeafe',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: '#2563eb', fontWeight: '600' }}>2</span>
                </div>
                <div>
                  <h3 style={{ 
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 0.25rem 0'
                  }}>
                    {artworkFailed ? 'Artwork Needs Re-sending' : 'Files Uploaded'}
                  </h3>
                  <p style={{ 
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    margin: 0
                  }}>
                    {artworkFailed
                      ? "Your images didn't finish uploading — see the note above"
                      : 'Your images have been securely uploaded to our business folder'}
                  </p>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ 
                  flexShrink: 0,
                  width: '32px',
                  height: '32px',
                  background: '#dcfce7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: '#059669', fontWeight: '600' }}>3</span>
                </div>
                <div>
                  <h3 style={{ 
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 0.25rem 0'
                  }}>
                    {receiptFailed ? 'Confirmation Email Delayed' : 'Receipts Sent'}
                  </h3>
                  <p style={{ 
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    margin: 0
                  }}>
                    {receiptFailed
                      ? "We couldn't send your confirmation email yet — see the note above"
                      : 'Order confirmations have been sent to you and our production team'}
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ 
                  flexShrink: 0,
                  width: '32px',
                  height: '32px',
                  background: '#dcfce7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: '#059669', fontWeight: '600' }}>4</span>
                </div>
                <div>
                  <h3 style={{ 
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 0.25rem 0'
                  }}>
                    Production Begins
                  </h3>
                  <p style={{ 
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    margin: 0
                  }}>
                    We'll begin processing your custom Press Peels within 1-2 business days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={{ 
          background: '#eff6ff',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ 
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '1rem',
            margin: '0 0 1rem 0'
          }}>
            📞 Need Help or Have Questions?
          </h2>
          <div style={{ color: '#1e3a8a' }}>
            <p style={{ marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
              If you have any questions about your business order, please contact us:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <p style={{ margin: 0 }}>
                <strong>Email:</strong> support@presspeels.com
              </p>
              <p style={{ margin: 0 }}>
                <strong>Reference:</strong> {displayOrderNumber ? `Order #${displayOrderNumber}` : 'Your order number'}
              </p>
            </div>
          </div>
        </div>

        {/* Application Instructions */}
        <div style={{ 
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '2rem'
        }}>
          <h2 style={{ 
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '1.5rem',
            textAlign: 'center',
            margin: '0 0 1.5rem 0'
          }}>
            🔥 Press Peels Application Instructions
          </h2>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: '2rem'
          }}>
            <div>
              <h3 style={{ 
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '1rem',
                margin: '0 0 1rem 0'
              }}>
                Settings
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ 
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Temperature:
                  </span>
                  <span style={{ color: '#111827' }}>
                    302°F (150°C)
                  </span>
                </div>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ 
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Pressure:
                  </span>
                  <span style={{ color: '#111827' }}>
                    Medium to High
                  </span>
                </div>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ 
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Time:
                  </span>
                  <span style={{ color: '#111827' }}>
                    10-15 seconds
                  </span>
                </div>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ 
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Peel:
                  </span>
                  <span style={{ color: '#111827' }}>
                    Hot peel (immediate)
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 style={{ 
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '1rem',
                margin: '0 0 1rem 0'
              }}>
                Pro Tips
              </h3>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                color: '#374151'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#059669', marginRight: '0.5rem' }}>✓</span>
                  Pre-press garment for 2-3 seconds to remove moisture
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#059669', marginRight: '0.5rem' }}>✓</span>
                  Place Press Peels face down on garment
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#059669', marginRight: '0.5rem' }}>✓</span>
                  Use parchment paper or Teflon sheet on top
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#059669', marginRight: '0.5rem' }}>✓</span>
                  Apply even pressure across entire design
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex',
          flexDirection: isNarrow ? 'column' : 'row',
          gap: '1rem',
          justifyContent: 'center',
          marginTop: '2rem'
        }}>
          <a
            href="/biz"
            style={{
              padding: '0.75rem 1.5rem',
              background: '#2563eb',
              color: 'white',
              borderRadius: '6px',
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              display: 'inline-block'
            }}
          >
            Submit Another Business Order
          </a>
          <a
            href="/"
            style={{
              padding: '0.75rem 1.5rem',
              background: '#4b5563',
              color: 'white',
              borderRadius: '6px',
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              display: 'inline-block'
            }}
          >
            Return to Main Site
          </a>
        </div>
      </div>
    </div>
  );
} 